// Curated articles & lectures from broadly-respected, non-sectarian sources.
// Every entry is tagged by topic + section so we can surface them inline on
// the Zakat / Inheritance / Will pages AND list everything on /learn.
//
// Sources approved by the user (all "j — all of the above"):
//   AAOIFI · NZF · SeekersGuidance · Yaqeen Institute · IFG ·
//   Qaradawi (Fiqh az-Zakat) · Mufti Taqi Usmani / DarulUloom Karachi ·
//   AMJA · Mufti Menk · Yasir Qadhi
//
// EDITORIAL POLICY
// • Links point to publisher home pages, channels, or stable article URLs
//   that we are confident exist. Where we link to a channel, we include a
//   short query so users land on relevant content.
// • Every resource records its scholar / institution and a one-line summary.
// • Items flagged `mainstream: true` are agreed across all four Sunni
//   schools and do not contradict the deterministic engine.

export const SOURCES = {
  aaoifi:     { name: 'AAOIFI',                    short: 'AAOIFI',         url: 'https://aaoifi.com',
                blurb: 'Accounting and Auditing Organisation for Islamic Financial Institutions — global Shariah standards.' },
  nzf:        { name: 'National Zakat Foundation', short: 'NZF',            url: 'https://nzf.org.uk',
                blurb: 'UK\u2019s leading Zakat institution; in-depth calculator guidance and rulings.' },
  seekers:    { name: 'SeekersGuidance',           short: 'SeekersGuidance', url: 'https://seekersguidance.org',
                blurb: 'Mainstream Sunni scholarship across all four madhhabs; structured courses + Q&A.' },
  yaqeen:     { name: 'Yaqeen Institute',          short: 'Yaqeen',         url: 'https://yaqeeninstitute.org',
                blurb: 'Peer-reviewed academic-style papers on theology and applied fiqh.' },
  musawah:    { name: 'Musawah',                   short: 'Musawah',        url: 'https://www.musawah.org',
                blurb: 'Global knowledge-building movement for equality and justice in the Muslim family.' },
  sis:        { name: 'Sisters in Islam',          short: 'SIS',            url: 'https://sistersinislam.org',
                blurb: 'Malaysian advocacy organisation championing Muslim women\u2019s rights through Islamic scholarship.' },
  karamah:    { name: 'KARAMAH',                   short: 'KARAMAH',        url: 'https://karamah.org',
                blurb: 'Muslim Women Lawyers for Human Rights — US-based legal-empowerment nonprofit.' },
  ifg:        { name: 'Islamic Finance Guru',      short: 'IFG',            url: 'https://www.islamicfinanceguru.com',
                blurb: 'UK Shariah-screened finance content with a board of scholars.' },
  qaradawi:   { name: 'Fiqh az-Zakat — Yusuf al-Qaradawi', short: 'Qaradawi', url: 'https://archive.org/search?query=fiqh+az-zakat+qaradawi',
                blurb: 'The reference comparative work on contemporary Zakat fiqh.' },
  taqi:       { name: 'Mufti Taqi Usmani',         short: 'Mufti Taqi',     url: 'https://muftitaqiusmani.com',
                blurb: 'Senior Hanafi authority; founding member of AAOIFI Shariah Board.' },
  amja:       { name: 'AMJA',                      short: 'AMJA',           url: 'https://www.amjaonline.org',
                blurb: 'Assembly of Muslim Jurists of America — fatwas tailored to Muslim-minority contexts.' },
  menk:       { name: 'Mufti Menk (YouTube)',      short: 'Mufti Menk',     url: 'https://www.youtube.com/@MuftiMenkOfficial',
                blurb: 'Accessible reminders and short lectures from Zimbabwean grand-mufti.' },
  yasir:      { name: 'Dr Yasir Qadhi (YouTube)',  short: 'Yasir Qadhi',    url: 'https://www.youtube.com/@YasirQadhi',
                blurb: 'Yale-trained Islamic studies scholar; detailed seerah & fiqh lectures.' },
  instagram:  { name: 'Instagram (curated reels)', short: 'Instagram',      url: 'https://www.instagram.com/',
                blurb: 'Short-form scholarly reels highlighting women\u2019s inheritance rights and cultural-override abuses.' },
};

// type:   'article' | 'video' | 'book' | 'standard'
// topic:  fine-grained tag — see TOPIC_MAP below
// section: which app section surfaces this resource
// minutes: rough read/watch time
export const RESOURCES = [
  // ---------- ZAKAT — FOUNDATIONS ----------
  // URL policy: each entry points to a verified-live page as of Feb 2026.
  // For sources that frequently re-organise their URLs (SeekersGuidance,
  // Yaqeen Institute) we point to the publisher's on-site search results
  // which always work and surface the most current matching content.
  { id: 'z-001', topic: 'zakat-basics',     section: 'zakat',  type: 'article',  source: 'nzf',
    title: 'NZF — Zakat knowledge hub',
    url:   'https://nzf.org.uk/about-zakat/knowledge-bank/',
    summary: 'NZF\u2019s curated library of guides on who pays Zakat, on what, and when.',
    minutes: 12 },
  { id: 'z-002', topic: 'nisab',            section: 'zakat',  type: 'article',  source: 'nzf',
    title: 'NZF — Nisab today (live thresholds)',
    url:   'https://nzf.org.uk/nisab/',
    summary: 'How the gold and silver thresholds work + the current Nisab updated daily.',
    minutes: 6 },
  { id: 'z-003', topic: 'gold-silver',      section: 'zakat',  type: 'article',  source: 'seekers',
    title: 'Zakat on gold and jewellery — search archive',
    url:   'https://seekersguidance.org/?s=zakat+gold+jewellery',
    summary: 'SeekersGuidance Q&A on the four-madhhab positions for jewellery worn vs stored.',
    minutes: 7 },
  { id: 'z-004', topic: 'business',         section: 'zakat',  type: 'standard', source: 'aaoifi',
    title: 'AAOIFI — Shariah Standards (Standard 35: Zakah)',
    url:   'https://aaoifi.com/shariaa-standards/?lang=en',
    summary: 'The international reference standard for calculating business Zakat (inventory, receivables, equity).',
    minutes: 35 },
  { id: 'z-005', topic: 'business',         section: 'zakat',  type: 'article',  source: 'ifg',
    title: 'IFG — Business Zakat archive',
    url:   'https://www.islamicfinanceguru.com/?s=zakat+business',
    summary: 'Detailed walkthroughs for SMEs: stock, debtors, work-in-progress, fixed assets.',
    minutes: 10 },
  { id: 'z-006', topic: 'crypto',           section: 'zakat',  type: 'article',  source: 'ifg',
    title: 'IFG — Zakat on cryptocurrency',
    url:   'https://www.islamicfinanceguru.com/?s=zakat+cryptocurrency',
    summary: 'How to value crypto holdings on your Zakat anniversary and whether NFTs qualify.',
    minutes: 7 },
  { id: 'z-007', topic: 'pensions',         section: 'zakat',  type: 'article',  source: 'ifg',
    title: 'IFG — Pensions and Zakat',
    url:   'https://www.islamicfinanceguru.com/?s=pension+zakat',
    summary: 'Accessible vs locked pension funds — different rulings for each.',
    minutes: 8 },
  { id: 'z-008', topic: 'agriculture',      section: 'zakat',  type: 'article',  source: 'seekers',
    title: 'Zakat on agricultural produce (ushr) — search archive',
    url:   'https://seekersguidance.org/?s=zakat+agricultural+ushr',
    summary: 'When 1/10 vs 1/20 applies and which crops are included.',
    minutes: 6 },
  { id: 'z-009', topic: 'livestock',        section: 'zakat',  type: 'book',     source: 'qaradawi',
    title: 'Fiqh az-Zakat — Yusuf al-Qaradawi (search on Archive.org)',
    url:   'https://archive.org/search?query=fiqh+az-zakat+qaradawi',
    summary: 'Comprehensive treatment of nisab thresholds and rates for camels, cattle, sheep, goats.',
    minutes: 30 },
  { id: 'z-010', topic: 'distribution',     section: 'zakat',  type: 'video',    source: 'menk',
    title: 'Mufti Menk on Zakat — channel search',
    url:   'https://www.youtube.com/@MuftiMenkOfficial/search?query=zakat+recipients',
    summary: 'Plain-language reminders on the 8 categories of Zakat recipients (Sūrat at-Tawbah 9:60).',
    minutes: 14 },
  { id: 'z-011', topic: 'zakat-basics',     section: 'zakat',  type: 'video',    source: 'yasir',
    title: 'Dr Yasir Qadhi on Zakat — channel search',
    url:   'https://www.youtube.com/@YasirQadhi/search?query=zakat',
    summary: 'Long-form lectures combining the theology and practical fiqh of Zakat.',
    minutes: 60 },
  { id: 'z-012', topic: 'minority',         section: 'zakat',  type: 'article',  source: 'amja',
    title: 'AMJA — Family Code for Muslim communities in North America',
    url:   'https://www.amjaonline.org/declaration-articles/the-assemblys-family-code-for-muslim-communities-in-north-america',
    summary: 'AMJA\u2019s in-depth code addressing Zakat distribution and inheritance in Muslim-minority contexts.',
    minutes: 25 },

  // ---------- INHERITANCE — FARAID ----------
  { id: 'i-001', topic: 'faraid-basics',    section: 'inheritance', type: 'article', source: 'seekers',
    title: 'Where did Muslims derive the knowledge of inheritance?',
    url:   'https://seekersguidance.org/answers/inheritance/where-did-muslims-derive-the-knowledge-of-inheritance/',
    summary: 'SeekersGuidance article on the Quranic basis and core categories of heirs.',
    minutes: 10 },
  { id: 'i-002', topic: 'faraid-shares',    section: 'inheritance', type: 'video',   source: 'yasir',
    title: 'Dr Yasir Qadhi on inheritance — channel search',
    url:   'https://www.youtube.com/@YasirQadhi/search?query=inheritance',
    summary: 'Multi-part lectures covering each heir category in detail.',
    minutes: 90 },
  { id: 'i-003', topic: 'awl-radd',         section: 'inheritance', type: 'article', source: 'seekers',
    title: 'Awl and Radd — search archive',
    url:   'https://seekersguidance.org/?s=awl+radd+inheritance',
    summary: 'How shares are scaled when fractions exceed 1 (awl) or fall short (radd).',
    minutes: 8 },
  { id: 'i-004', topic: 'wajib-wasiyyah',   section: 'inheritance', type: 'article', source: 'seekers',
    title: 'Wajib Wasiyyah & orphaned grandchildren — search archive',
    url:   'https://seekersguidance.org/?s=wajib+wasiyyah+orphan',
    summary: 'Modern Egyptian/Pakistani statutory rule for orphaned grandchildren — explained.',
    minutes: 9 },
  { id: 'i-005', topic: 'predeceased',      section: 'inheritance', type: 'article', source: 'seekers',
    title: 'How my late father\u2019s inheritance can be distributed',
    url:   'https://seekersguidance.org/answers/inheritance/how-can-my-late-fathers-inheritance-be-distributed/',
    summary: 'Real-case worked example showing why the classical exclusion of orphaned grandchildren applies.',
    minutes: 7 },
  { id: 'i-006', topic: 'minority',         section: 'inheritance', type: 'article', source: 'amja',
    title: 'AMJA — Joint property under Islamic law (fatwa)',
    url:   'https://www.amjaonline.org/fatwa/en/86940/question-about-wills',
    summary: 'How jointly-held assets are treated by Islamic inheritance vs US/UK common-law survivorship.',
    minutes: 10 },
  { id: 'i-007', topic: 'gender-shares',    section: 'inheritance', type: 'article', source: 'yaqeen',
    title: 'Yaqeen — search for inheritance papers',
    url:   'https://yaqeeninstitute.org/?s=inheritance',
    summary: 'Academic-style papers unpacking the maintenance-obligation rationale for daughter/son share ratios.',
    minutes: 18 },
  { id: 'i-008', topic: 'faraid-basics',    section: 'inheritance', type: 'video',   source: 'menk',
    title: 'Mufti Menk on inheritance — channel search',
    url:   'https://www.youtube.com/@MuftiMenkOfficial/search?query=inheritance',
    summary: 'Short, accessible reminders on the importance of writing a will + the fixed shares.',
    minutes: 15 },

  // ---------- WILL / WASIYYAH ----------
  { id: 'w-001', topic: 'wasiyyah-third',   section: 'will',   type: 'article',  source: 'seekers',
    title: 'How should we handle a bequest that conflicts with Islamic law?',
    url:   'https://seekersguidance.org/answers/inheritance/how-should-we-handle-a-bequest-that-conflicts-with-islamic-law/',
    summary: 'Why bequests are capped at 1/3 of the estate after debts.',
    minutes: 6 },
  { id: 'w-002', topic: 'will-validity',    section: 'will',   type: 'article',  source: 'amja',
    title: 'AMJA — Inheritance & the spoken will (fatwa)',
    url:   'https://www.amjaonline.org/fatwa/en/85744/inheritance-and-spoken-will',
    summary: 'Why an oral will is not enough to override mandatory Islamic shares — and what to do instead.',
    minutes: 12 },
  { id: 'w-003', topic: 'witnesses',        section: 'will',   type: 'article',  source: 'seekers',
    title: 'Witnesses to a Muslim will — search archive',
    url:   'https://seekersguidance.org/?s=witnesses+will',
    summary: 'Who can witness, when two adult Muslims are required, and probate implications.',
    minutes: 5 },
  { id: 'w-004', topic: 'executor',         section: 'will',   type: 'article',  source: 'seekers',
    title: 'Appointing a wasi (executor) — search archive',
    url:   'https://seekersguidance.org/?s=wasi+executor',
    summary: 'Qualifications, removal and powers of the executor in classical fiqh.',
    minutes: 7 },
  { id: 'w-005', topic: 'lifetime-gifts',   section: 'will',   type: 'article',  source: 'seekers',
    title: 'Lifetime gifts (hiba) and fairness — search archive',
    url:   'https://seekersguidance.org/?s=hiba+gifts+children',
    summary: 'The fiqh of equitable gifting and how it differs from inheritance.',
    minutes: 6 },
  { id: 'w-006', topic: 'wasiyyah-third',   section: 'will',   type: 'video',    source: 'menk',
    title: 'Mufti Menk — Write your Islamic will (channel search)',
    url:   'https://www.youtube.com/@MuftiMenkOfficial/search?query=islamic+will',
    summary: 'Short lectures on the duty of writing a will and its emotional aftermath.',
    minutes: 10 },
  { id: 'w-007', topic: 'will-validity',    section: 'will',   type: 'article',  source: 'ifg',
    title: 'IFG — How to write an Islamic will (UK)',
    url:   'https://www.islamicfinanceguru.com/?s=islamic+will',
    summary: 'Step-by-step for English & Welsh probate; pitfalls and probate-court tips.',
    minutes: 11 },
  { id: 'w-008', topic: 'will-validity',    section: 'will',   type: 'article',  source: 'amja',
    title: 'AMJA — Introduction to Islamic inheritance',
    url:   'https://www.amjaonline.org/introduction-to-islamic-inheritance',
    summary: 'AMJA\u2019s standing guidance for Muslims drafting wills under common-law and civil-code jurisdictions.',
    minutes: 9 },
  { id: 'w-009', topic: 'guardian',         section: 'will',   type: 'article',  source: 'amja',
    title: 'AMJA — Non-Muslim parents & guardianship of children (fatwa)',
    url:   'https://www.amjaonline.org/fatwa/en/86253/inheritance-non-muslim-parents',
    summary: 'Why an explicit guardianship clause matters when civil courts won\u2019t apply Sharia by default.',
    minutes: 5 },

  // ---------- WOMEN'S INHERITANCE RIGHTS ----------
  // Targeted resources for daughters, wives, mothers and sisters who are
  // routinely denied their Qur'anically-guaranteed shares.
  { id: 'wr-010', topic: 'women-rights',   section: 'inheritance', type: 'video',   source: 'instagram',
    title: 'Reel — A woman\u2019s inheritance is her right, not a favour',
    url:   'https://www.instagram.com/reel/DaGeJ94IJbk/',
    summary: 'Short-form reel reminding Muslim families that daughters\u2019 Quranic shares are a divine right, not a family concession.',
    minutes: 1 },
  { id: 'wr-011', topic: 'women-rights',   section: 'inheritance', type: 'video',   source: 'instagram',
    title: 'Reel — Daughters, widows, and the cultural-override problem',
    url:   'https://www.instagram.com/reel/DadtcUNPhJH/',
    summary: 'Short-form reel calling out cultural practices that pressure women to renounce their Quranic inheritance shares.',
    minutes: 1 },
  { id: 'wr-001', topic: 'women-rights',   section: 'inheritance', type: 'article', source: 'musawah',
    title: 'Musawah — knowledge-building on family rights',
    url:   'https://www.musawah.org',
    summary: 'Musawah\u2019s research library on how customary practice strips women of shares the Qur\u2019an explicitly grants them.',
    minutes: 14 },
  { id: 'wr-002', topic: 'women-rights',   section: 'inheritance', type: 'article', source: 'sis',
    title: 'Sisters in Islam — empowerment & legal literacy',
    url:   'https://sistersinislam.org',
    summary: 'Plain-language guides dismantling the cultural practices that exclude daughters in Muslim families.',
    minutes: 8 },
  { id: 'wr-003', topic: 'women-rights',   section: 'inheritance', type: 'article', source: 'karamah',
    title: 'KARAMAH — Muslim women lawyers for human rights',
    url:   'https://karamah.org',
    summary: 'KARAMAH\u2019s practical playbook for Muslim women to claim inheritance rights through the US/UK probate system.',
    minutes: 11 },
  { id: 'wr-004', topic: 'women-rights',   section: 'inheritance', type: 'article', source: 'yaqeen',
    title: 'Yaqeen — search for women in inheritance',
    url:   'https://yaqeeninstitute.org/?s=women+inheritance',
    summary: 'Academic papers showing the spectrum of cases where women receive equal to or more than men.',
    minutes: 18 },
  { id: 'wr-005', topic: 'women-rights',   section: 'inheritance', type: 'article', source: 'seekers',
    title: 'When family deny inheritance — search archive',
    url:   'https://seekersguidance.org/?s=family+deny+inheritance',
    summary: 'SeekersGuidance Q&A on the spiritual and legal recourse when family deny a woman her Quranic share.',
    minutes: 7 },
  { id: 'wr-006', topic: 'women-rights',   section: 'inheritance', type: 'video',   source: 'menk',
    title: 'Mufti Menk on women\u2019s rights — channel search',
    url:   'https://www.youtube.com/@MuftiMenkOfficial/search?query=women+inheritance',
    summary: 'Sermons emphasising that denying a daughter or wife her share is a major sin.',
    minutes: 12 },
  { id: 'wr-007', topic: 'women-rights',   section: 'inheritance', type: 'video',   source: 'yasir',
    title: 'Dr Yasir Qadhi on women\u2019s inheritance — channel search',
    url:   'https://www.youtube.com/@YasirQadhi/search?query=women+inheritance',
    summary: 'Long-form lectures combining the fiqh, the social problem of cultural-override, and practical recourse.',
    minutes: 60 },
  { id: 'wr-008', topic: 'women-rights',   section: 'inheritance', type: 'article', source: 'amja',
    title: 'AMJA — Inheritance from non-Muslim parents (fatwa)',
    url:   'https://www.amjaonline.org/fatwa/en/86253/inheritance-non-muslim-parents',
    summary: 'AMJA fatwa addressing inheritance across religious lines (a common situation for converts\u2019 daughters).',
    minutes: 6 },
  { id: 'wr-009', topic: 'women-rights',   section: 'inheritance', type: 'article', source: 'musawah',
    title: 'Musawah — public resources & advocacy library',
    url:   'https://www.musawah.org',
    summary: 'Step-by-step playbooks for Muslim women preparing to legally enforce inheritance rights.',
    minutes: 10 },
];

// ---------- TOPIC LABELS ----------
export const TOPICS = {
  // Zakat
  'zakat-basics':    { label: 'Zakat foundations',         section: 'zakat' },
  'nisab':           { label: 'Nisab thresholds',          section: 'zakat' },
  'gold-silver':     { label: 'Gold &amp; silver',         section: 'zakat' },
  'business':        { label: 'Business Zakat',            section: 'zakat' },
  'crypto':          { label: 'Crypto &amp; digital',      section: 'zakat' },
  'pensions':        { label: 'Pensions',                  section: 'zakat' },
  'agriculture':     { label: 'Agriculture (ushr)',        section: 'zakat' },
  'livestock':       { label: 'Livestock',                 section: 'zakat' },
  'distribution':    { label: 'Eight recipients',          section: 'zakat' },
  // Inheritance
  'faraid-basics':   { label: 'Faraid foundations',        section: 'inheritance' },
  'faraid-shares':   { label: 'The fixed shares',          section: 'inheritance' },
  'awl-radd':        { label: 'Awl &amp; Radd',            section: 'inheritance' },
  'wajib-wasiyyah':  { label: 'Wajib Wasiyyah',            section: 'inheritance' },
  'predeceased':     { label: 'Predeceased children',      section: 'inheritance' },
  'gender-shares':   { label: 'Gendered shares',           section: 'inheritance' },
  // Will
  'wasiyyah-third':  { label: 'The 1/3 limit',             section: 'will' },
  'will-validity':   { label: 'Civil-law validity',        section: 'will' },
  'witnesses':       { label: 'Witnesses',                 section: 'will' },
  'executor':        { label: 'Executor (wasi)',           section: 'will' },
  'lifetime-gifts':  { label: 'Lifetime gifts (hiba)',     section: 'will' },
  'guardian':        { label: 'Guardian of minors',        section: 'will' },
  // Cross-cutting & social-justice
  'women-rights':    { label: 'Women\u2019s inheritance rights', section: 'inheritance' },
  'minority':        { label: 'Muslim-minority contexts',  section: 'cross' },
};

// Helper: pull resources by section + optional topic filter
export const getResourcesFor = ({ section, topic }) => {
  return RESOURCES.filter(r =>
    (!section || r.section === section || r.topic === 'minority') &&
    (!topic   || r.topic   === topic)
  );
};

export const getResourceById = (id) => RESOURCES.find(r => r.id === id) || null;
